/* eslint-disable jsx-a11y/label-has-associated-control */
import { Fragment, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { Page } from '../../components';
import Notification from '../../components/Notification';
import { ReactComponent as RadioChecked } from '../../assets/images/radio-button-checked.svg';
import { ReactComponent as RadioUnchecked } from '../../assets/images/radio-button-unchecked.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/info-stroke.svg';
import { ReactComponent as UploadIcon } from '../../assets/images/upload-white.svg';
import useMappingToolStore from './store';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 8 }, (_, index) => {
  const year = CURRENT_YEAR - index;
  return { value: year, label: String(year) };
});

const SELECT_MENU_MAX_HEIGHT = 220;

const STEPS = [
  { key: 'upload', label: 'Upload file' },
  { key: 'mapping', label: 'Mapping' },
  { key: 'review', label: 'Review' },
  { key: 'testing', label: 'Testing' }
];

const LIST_PATH = '/report_template_mapper/list';
const MAPPING_PATH = '/report_template_mapper/tool';

const notifyResult = result => {
  if (!result) return;
  toast.info(<Notification title={result.ok ? 'Success' : 'Fail'} text={result.message} />);
};

const UploadForm = () => {
  const history = useHistory();
  const fileInputRef = useRef(null);
  const [
    {
      file: selectedFile,
      formState: state,
      formYear,
      formName,
      mappingVersion,
      stateOptions,
      stateOptionsLoading,
      stateOptionsError,
      stateFormId,
      uploadLoading
    },
    {
      setFile,
      setFormState,
      setFormYear,
      setFormName,
      setMappingVersion,
      fetchStates,
      uploadStateForm,
      deleteStateForm,
      clearMapping
    }
  ] = useMappingToolStore();
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  // Cancelling deletes the uploaded PDF and clears every field, so the next run starts clean.
  const cancelMapping = () => {
    deleteStateForm().then(notifyResult);
    clearMapping();
    history.push(LIST_PATH);
  };
  // The upload happens here, not when the file is picked, so the record is created with the
  // details as they finally stand. A rejected upload keeps the user on this step -- the fix is
  // a different file or a new mapping version, both of which live on this screen.
  const startMapping = async () => {
    // An id already set means a previous attempt succeeded (e.g. the user came back from
    // Mapping). Drop that record before re-uploading so the details can't drift from it and
    // the replaced upload isn't orphaned on the server.
    if (stateFormId) await deleteStateForm();
    const result = await uploadStateForm(selectedFile);
    // notifyResult carries the backend's own message; a rejected upload just keeps the user
    // here, where changing the file or the mapping version is what unblocks them.
    notifyResult(result);
    if (!result?.ok) return;
    history.push(MAPPING_PATH);
  };

  const handleFiles = fileList => {
    const file = fileList?.[0];
    if (file && file.type === 'application/pdf') {
      setFile(file);
    }
  };

  const openFileBrowser = () => fileInputRef.current?.click();

  const removeFile = () => {
    if (stateFormId) deleteStateForm().then(notifyResult);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const detailsComplete = Boolean(state && formYear && formName.trim().length >= 10);
  const isNextStepDisabled = !selectedFile || !detailsComplete || uploadLoading;

  return (
    <Page
      documentName="Inspection Report Template Mapper"
      pageName="Inspection Report Template Mapper - Upload File"
      path="/report_template_mapper/upload"
    >
      <div className="mapping-tool-page">
        <div className="mapping-tool-header d-flex align-items-center justify-content-between">
          <div>
            <h1 className="mapping-tool-title">Inspection Report Template Mapper - Upload File</h1>
            <p className="mapping-tool-subtitle">
              Fulfill the information below and upload a new Form
            </p>
          </div>
          <div className="upload-form-header-actions">
            <button type="button" className="btn upload-form-cancel-btn" onClick={cancelMapping}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary mapping-tool-new-btn"
              disabled={isNextStepDisabled}
              onClick={startMapping}
            >
              {uploadLoading ? 'Uploading…' : 'Start Mapping'}
            </button>
          </div>
        </div>

        <div className="mapping-tool-list-card">
          <div className="upload-form-stepper">
            {STEPS.map((step, index) => (
              <Fragment key={step.key}>
                <div className="upload-form-step">
                  {index === 0 ? (
                    <RadioChecked className="upload-form-step-icon" />
                  ) : (
                    <RadioUnchecked className="upload-form-step-icon" />
                  )}
                  <span className="upload-form-step-label">{step.label}</span>
                </div>
                {index < STEPS.length - 1 && <span className="upload-form-step-line" />}
              </Fragment>
            ))}
          </div>
        </div>

        <div className="upload-form-info-banner">
          <InfoIcon className="upload-form-info-icon" />
          <div className="upload-form-info-text">
            <ol>
              <li>
                Enter Inspection Form Details: Select the state, the year, the version and write the
                inspection form name.
              </li>
              <li>Upload PDF: Upload the corresponding file.</li>
            </ol>
          </div>
        </div>

        <div className="mapping-tool-list-card upload-form-fields">
          <div className="upload-form-field">
            <label htmlFor="upload-form-state">State:</label>
            <Select
              inputId="upload-form-state"
              classNamePrefix="upload-form-select"
              placeholder="Select state"
              isLoading={stateOptionsLoading}
              options={stateOptions}
              value={stateOptions.find(option => option.label === state) ?? null}
              onChange={option => setFormState(option?.label ?? '', option?.value ?? '')}
              maxMenuHeight={SELECT_MENU_MAX_HEIGHT}
              menuPortalTarget={document.body}
              required
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
            {stateOptionsError && <p className="upload-form-field-error">{stateOptionsError}</p>}
          </div>
          <div className="upload-form-field">
            <label htmlFor="upload-form-year">Inspection Form&apos;s year:</label>
            <Select
              inputId="upload-form-year"
              classNamePrefix="upload-form-select"
              placeholder="Select year"
              options={YEAR_OPTIONS}
              value={YEAR_OPTIONS.find(option => option.value === formYear) ?? null}
              onChange={option => setFormYear(option?.value ?? '')}
              maxMenuHeight={SELECT_MENU_MAX_HEIGHT}
              menuPortalTarget={document.body}
              required
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
          <div className="upload-form-field upload-form-field-narrow">
            <label htmlFor="upload-form-mapping-version">Mapping Version:</label>
            <input
              id="upload-form-mapping-version"
              type="number"
              className="form-control"
              min="1"
              step="1"
              placeholder="e.g. 1"
              value={mappingVersion}
              required
              onChange={e => setMappingVersion(e.target.value)}
            />
          </div>
          <div className="upload-form-field upload-form-field-grow">
            <label htmlFor="upload-form-name">Inspection Form&apos;s name:</label>
            <input
              id="upload-form-name"
              type="text"
              className="form-control"
              placeholder="Type inspection form's name"
              minLength={10}
              value={formName}
              required
              onChange={e => setFormName(e.target.value)}
            />
          </div>
        </div>

        {detailsComplete && (
          <div
            className={`upload-form-dropzone ${isDragging ? 'dragging' : ''}`}
            onClick={openFileBrowser}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') openFileBrowser();
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              style={{ display: 'none' }}
              onChange={e => handleFiles(e.target.files)}
            />
            {selectedFile ? (
              <div className="upload-form-dropzone-selected">
                <span className="upload-form-dropzone-selected-name">{selectedFile.name}</span>
                <button
                  type="button"
                  className="upload-form-dropzone-selected-remove"
                  aria-label="Remove file"
                  onClick={e => {
                    e.stopPropagation();
                    removeFile();
                  }}
                >
                  ×
                </button>
                {uploadLoading && <p className="upload-form-dropzone-subtitle">Uploading…</p>}
              </div>
            ) : (
              <>
                <div className="upload-form-dropzone-icon">
                  <UploadIcon />
                </div>
                <p className="upload-form-dropzone-title">Choose a PDF</p>
                <p className="upload-form-dropzone-subtitle">
                  <span
                    className="upload-form-dropzone-link"
                    onClick={e => {
                      e.stopPropagation();
                      openFileBrowser();
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        openFileBrowser();
                      }
                    }}
                  >
                    Click here to upload
                  </span>
                  {' or Drag/Drop files here.'}
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </Page>
  );
};

export default UploadForm;
